import * as React from 'react';

import type { UploadFileMediaCredentials } from 'rest/portal-media';
import { getUploadCredentialsForRequest, getCredentialsForMediaRequest } from 'rest/portal-media/portal-media';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { AuthProvider } from '@atlaskit/media-core';

export interface WithUploadCredentialsProps {
    portalId: number;
    requestId?: number;
    children: (token: Promise<MediaProvider>) => React.ReactElement | null;
}

const credentialsAreValid = (credentials: UploadFileMediaCredentials): boolean => {
    // We check that the credentials exist and they won't expire within the next 15 seconds to ensure they won't expire during the upload (most likely)
    const fifteenSecondsAfterNow = new Date().getTime() + 15000;
    return credentials.expiryEpoch > fifteenSecondsAfterNow;
};

export const WithUploadCredentials = ({ portalId, requestId, children }: WithUploadCredentialsProps) => {
    const credentialsRef = React.useRef<UploadFileMediaCredentials | null>(null);
    const lastFetchedPortalIdRef = React.useRef<number>(portalId);
    const lastFetchedRequestIdRef = React.useRef<number | undefined>(requestId);
    const getCredentials = React.useCallback(async (): Promise<UploadFileMediaCredentials> => {
        // If we haven't fetched credentials or they're no longer valid, update them
        if (
            !credentialsRef.current ||
            !credentialsAreValid(credentialsRef.current) ||
            lastFetchedPortalIdRef.current !== portalId ||
            lastFetchedRequestIdRef.current !== requestId
        ) {
            lastFetchedPortalIdRef.current = portalId;
            lastFetchedRequestIdRef.current = requestId;
            if (FeatureGates.checkGate('request_create_inline_attachment') && !requestId) {
                credentialsRef.current = await getCredentialsForMediaRequest(portalId);
                return credentialsRef.current;
            }
            // Request Id can be undefined but with removal of request_create_inline_attachment type casting can be removed
            credentialsRef.current = await getUploadCredentialsForRequest(portalId, requestId as number);
        }

        return credentialsRef.current;
    }, [portalId, requestId]);

    const getMediaProvider = React.useCallback(async (): Promise<MediaProvider> => {
        const authProvider: AuthProvider = async () => {
            const latestCredentials = await getCredentials();
            return {
                clientId: latestCredentials.clientId,
                token: latestCredentials.token,
                baseUrl: latestCredentials.endpointUrl,
            };
        };

        const credentials = await getCredentials();
        return {
            uploadParams: {
                collection: credentials.targetCollection,
            },
            viewMediaClientConfig: {
                authProvider,
                useSha256ForUploads: true,
            },
            uploadMediaClientConfig: {
                authProvider,
                useSha256ForUploads: true,
            },
        };
    }, [getCredentials]);

    const mediaProviderPromise = React.useMemo(() => getMediaProvider(), [getMediaProvider]);

    return FeatureGates.checkGate('memoize_media_provider_promise')
        ? children(mediaProviderPromise)
        : children(getMediaProvider());
};
